import { useEffect, useState, useCallback } from 'react';
import {
  FaList,
  FaPalette,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';

import CustomFormGroup from '../components/CustomFormGroup';
import MessageModal from '../components/MessageModal';
import Pagination from '../components/Pagination';
import ModalConfirm from '../components/ModalConfirm';
import Table from '../components/Table';
import CommonButton from '../components/CommonButton';
import { useLoading } from "../components/LoadingContext";

import {
  addSkill,
  getSkills,
  updateSkill,
  deleteSkill
} from '../services/api/skillService';

import '../styles/Skill.css';

const LIMIT_OPTIONS = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: 'All', value: 'All' }
];

const SkillPage = () => {
  const { showLoading, hideLoading } = useLoading();

  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedSkill, setSelectedSkill] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ---------- helpers ----------
  const resetForm = () => {
    setForm({ name: '', description: '' });
    setErrors({});
    setSelectedSkill(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- data ----------
  const fetchSkills = useCallback(async () => {
    try {
      const offset = limit === 'All' ? 0 : (currentPage - 1) * limit;
      const { data, count } = await getSkills(searchTerm, limit, offset);

      setSkills(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Fetch skills failed:', err);
      setSkills([]);
      setTotalCount(0);
    }
  }, [searchTerm, limit, currentPage]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // ---------- handlers ----------
  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (id) => {
    const skill = skills.find(s => s.id === id);
    if (!skill) return;

    setSelectedSkill(skill);
    setForm({
      name: skill.name,
      description: skill.description || ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSaving || !validate()) return;

    showLoading();
    setIsSaving(true);

    try {
      const result = selectedSkill
        ? await updateSkill(selectedSkill.id, form.name, form.description)
        : await addSkill(form.name, form.description);

      if (result?.success === false) {
        setErrors({ form: result.message || 'Failed to save skill' });
        return;
      }

      await fetchSkills();
      setIsModalOpen(false);
      setIsMessageModalOpen(true);
    } catch (err) {
      console.error(err);
      setErrors({ form: 'Unexpected error occurred' });
    } finally {
      setIsSaving(false);
      hideLoading();
    }
  };

  const confirmDelete = (id) => {
    const skill = skills.find(s => s.id === id);
    if (!skill) return;

    setSelectedSkill(skill);
    setDeleteError('');
    setIsResponseModalOpen(false);
    setIsConfirmOpen(true);
  };

  const adjustPageAfterDelete = () => {
    const newTotalCount = totalCount - 1;
    const newTotalPages =
      limit === 'All' ? 1 : Math.ceil(newTotalCount / limit);

    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages || 1);
    }
  };

  const handleDelete = async () => {
    if (isDeleting || !selectedSkill) return;

    showLoading();
    setIsDeleting(true);

    try {
      const result = await deleteSkill(selectedSkill.id);

      if (!result?.success) {
        setDeleteError(result?.message || 'Failed to delete skill');
      } else {
        adjustPageAfterDelete();
        await fetchSkills();
      }

      setIsResponseModalOpen(true);
    } catch (err) {
      console.error(err);
      setDeleteError('Unexpected error occurred');
      setIsResponseModalOpen(true);
    } finally {
      setIsDeleting(false);
      hideLoading();
    }
  };

  // ---------- UI ----------
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' }
  ];

  return (
    <div className="skill-page-container">
      <h2>Skills</h2>

      <div className="skill-controls">
        <CustomFormGroup
          type="select"
          options={LIMIT_OPTIONS}
          value={limit}
          onChange={(e) => {
            const v = e.target.value;
            setLimit(v === 'All' ? 'All' : Number(v));
            setCurrentPage(1);
          }}
          icon={<FaList />}
        />

        <CustomFormGroup
          type="text"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          icon={<FaSearch />}
        />
      </div>

      <Table
        columns={columns}
        data={skills}
        onEdit={openEditModal}
        onDelete={confirmDelete}
      />

      <div className="new-skill-button-container">
        <CommonButton onClick={openAddModal}>+ New Skill</CommonButton>
      </div>

      {/* ADD / EDIT MODAL */}
      {isModalOpen && (
        <div className="skill-modal-overlay">
          <div className="skill-modal-content">
            <h3>{selectedSkill ? 'Edit Skill' : 'New Skill'}</h3>

            <form onSubmit={handleSave}>
              <CustomFormGroup
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Skill Name"
                icon={<FaPalette />}
                error={errors.name}
              />

              <CustomFormGroup
                type="textarea"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Skill Description"
                icon={<FaList />}
              />

              <CommonButton type="submit" loading={isSaving}>
                Save
              </CommonButton>

              <CommonButton
                isCancel
                disabled={isSaving}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </CommonButton>
            </form>
          </div>
        </div>
      )}

      {isMessageModalOpen && (
        <MessageModal
          title="Successfully Saved"
          message_content="Your data changes were successfully saved."
          icon={<FaCheckCircle />}
          button_name="Close"
          isSuccess
          onClose={() => setIsMessageModalOpen(false)}
        />
      )}

      {isConfirmOpen && selectedSkill && (
        <ModalConfirm
          title="Delete Confirmation"
          message={`Are you sure want to delete ${selectedSkill.name}?`}
          onClickYes={handleDelete}
          onClickNo={() => setIsConfirmOpen(false)}
          isResponseModal={isResponseModalOpen}
          responseTitle={deleteError ? 'Failed' : 'Success'}
          responseMessage={
            deleteError
              ? `Failed to delete ${selectedSkill.name} due to: ${deleteError}`
              : `Successfully deleted ${selectedSkill.name}`
          }
          icon={deleteError ? <FaTimesCircle /> : <FaCheckCircle />}

          isLoading={isDeleting}
          onResponseClose={() => {
            setIsResponseModalOpen(false);
            setIsConfirmOpen(false);
          }}
        />
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={limit === 'All' ? 1 : Math.ceil(totalCount / limit)}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default SkillPage;
